import { motion } from "framer-motion";
import { WhatsappLogo, InstagramLogo, SignIn, UserPlus, DeviceMobile } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen">

      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 glass-header shadow-sm"
        data-testid="header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <motion.h1
            className="text-2xl sm:text-3xl font-black tracking-tight text-[#042F2E]"
            data-testid="club-logo"
          >
            MARABU OPEN CLUB
          </motion.h1>
          <div className="flex gap-3">
            {user ? (
              <Button
                onClick={() => navigate("/dashboard")}
                className="bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full px-6"
                data-testid="dashboard-button"
              >
                Minha Conta
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => navigate("/login?tab=login")}
                  variant="outline"
                  className="border-[#FF5A26] text-[#FF5A26] hover:bg-[#FF5A26] hover:text-white font-bold rounded-full px-6"
                  data-testid="login-button"
                >
                  <SignIn size={20} weight="bold" className="mr-2" />
                  Entrar
                </Button>
                <Button
                  onClick={() => navigate("/login?tab=register")}
                  className="bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full px-6"
                  data-testid="register-button"
                >
                  <UserPlus size={20} weight="bold" className="mr-2" />
                  Cadastre-se
                </Button>
              </>
            )}
          </div>
        </div>
      </motion.header>

      {/* Hero Section with Video */}
      <section className="relative h-[85vh] mt-16 overflow-hidden" data-testid="hero-section">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          poster="https://customer-assets.emergentagent.com/job_aqua-membership-1/artifacts/eaov1h5n_WhatsApp%20Image%202026-04-07%20at%2020.51.21.jpeg"
        >
          <source src="https://customer-assets.emergentagent.com/job_aqua-membership-1/artifacts/e5vp6o85_WhatsApp%20Video%202026-04-07%20at%2020.15.39%20%281%29.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="max-w-4xl"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight" data-testid="hero-title">
              Bem-vindo ao MARABU OPEN CLUB
            </h1>
            <p className="text-xl sm:text-2xl text-white/95 mb-10 font-medium leading-relaxed">
              Seu clube de lazer, esporte e diversão
            </p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={() => user ? navigate("/dashboard") : navigate("/login?tab=register")}
                className="bg-white text-[#FF5A26] hover:bg-[#F4F1EA] font-bold rounded-full px-10 py-7 text-lg shadow-2xl"
                data-testid="hero-cta-button"
              >
                {user ? "Acessar Minha Conta" : "Cadastre-se Agora"}
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Social Links Section */}
      <section className="bg-[#F4F1EA] py-16 sm:py-20" data-testid="social-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#042F2E] mb-4">
              Conecte-se Conosco
            </h2>
            <p className="text-base sm:text-lg text-[#115E59]">
              Fale conosco ou siga nas redes sociais
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <motion.a
              href="https://wa.me/5521984317457"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03, y: -4 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center justify-center gap-4 bg-[#25D366] hover:bg-[#20BA5A] text-white font-bold rounded-2xl p-8 shadow-lg transition-all duration-200"
              data-testid="whatsapp-link"
            >
              <WhatsappLogo size={40} weight="fill" />
              <span className="text-xl">Fale no WhatsApp</span>
            </motion.a>

            <motion.a
              href="https://www.instagram.com/marabuclub_?igsh=MWxxMHNlY2UwZXBpMA%3D%3D&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03, y: -4 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center justify-center gap-4 bg-gradient-to-br from-[#E1306C] via-[#C13584] to-[#833AB4] hover:opacity-90 text-white font-bold rounded-2xl p-8 shadow-lg transition-all duration-200"
              data-testid="instagram-link"
            >
              <InstagramLogo size={40} weight="fill" />
              <span className="text-xl">Siga no Instagram</span>
            </motion.a>
          </div>
        </div>
      </section>

      {/* Install App Section */}
      <section className="bg-[#042F2E] py-16 sm:py-20" data-testid="install-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <DeviceMobile size={48} weight="duotone" className="text-[#FF5A26] mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Baixe Nosso App
            </h2>
            <p className="text-base sm:text-lg text-white/80 mb-8 max-w-xl mx-auto">
              Instale o MARABU OPEN CLUB no seu celular e tenha tudo na palma da mão
            </p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={() => navigate("/instalar")}
                className="bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full px-10 py-7 text-lg shadow-2xl"
                data-testid="install-app-button"
              >
                <DeviceMobile size={24} weight="bold" className="mr-3" />
                Baixar App Grátis
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#042F2E] text-white py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold mb-2">MARABU OPEN CLUB</h3>
          <p className="text-white/80">2026 Todos os direitos reservados</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
